import { createTheme } from "@mui/material";

export const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 665,
            md: 1000,
            lg: 1180,
            xl: 1920,
        },
    },

    palette: {
        neutral: {
            100: "#F3F4F6",
            200: "#E5E7EB",
            300: "#D1D5DB",
            400: "#9CA3AF",
            500: "#6B7280",
            600: "#4B5563",
            700: "#374151",
            800: "#1F2937",
            900: "#111827",
        },
        action: {
            active: "#6B7280",
            focus: "rgba(55, 65, 81, 0.12)",
            hover: "rgba(55, 65, 81, 0.04)",
            selected: "rgba(55, 65, 81, 0.08)",
            disabledBackground: "rgba(55, 65, 81, 0.12)",
            disabled: "rgba(55, 65, 81, 0.26)",
        },
        background: {
            default: "#FFFFFF",
            paper: "#FFFFFF",
        },
        divider: "#E6E8F0",
        primary: {
            main: "#0080ff",
            light: "#828DF8",
            dark: "#0066CC",
            contrastText: "#FFFFFF",
        },
        secondary: {
            main: "",
            light: "#3FC79A",
            dark: "#0B815A",
            contrastText: "#FFFFFF",
        },
        success: {
            main: "#14B8A6",
            light: "#43C6B7",
            dark: "#0E8074",
            contrastText: "#FFFFFF",
        },
        info: {
            main: "#2196F3",
            light: "#64B6F7",
            dark: "#0B79D0",
            contrastText: "#FFFFFF",
        },
        warning: {
            main: "#FFB020",
            light: "#FFBF4C",
            dark: "#B27B16",
            contrastText: "#FFFFFF",
        },
        error: {
            main: "#D14343",
            light: "#DA6868",
            dark: "#922E2E",
            contrastText: "#FFFFFF",
        },
        dark: {
            main: "#000000",
            light: "rgba(0,0,0,0.1)",
            dark: "#282C35",
            contrastText: "#FFFFFF",
        },
        text: {
            primary: "#000000",
            main: "#0080ff",
            secondary: "#667085",
            success: "#099250",
            disabled: "#909090",
        },
    },
    components: {
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    background: "#000000 !important",
                    "& *": { color: "rgba(255, 255, 255)" },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                paper: {
                    background: "#ffffff !important",
                    borderRadius: 10,
                },
            },
        },

        MuiLink: {
            styleOverrides: {
                root: {
                    cursor: "pointer",
                    color: "text.main",
                    fontSize: "0.85rem",
                    fontWeight: 400,
                    letterSpacing: 1,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    fontSize: "0.90rem",
                    fontWeight: 400,
                    letterSpacing: 1,
                },
            },
        },

        MuiTab: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                labelIcon: {
                    minWidth: "auto",
                    textTransform: "inherit",
                    padding: "12px 15px",
                    borderRadius: "0px",
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: "none",
                    color: "text.main",
                    borderColor: "text.main",
                },

                sizeSmall: {
                    padding: "2px 10px",
                },
                sizeMedium: {
                    padding: "8px 20px",
                },
                sizeLarge: {
                    padding: "8px 30px",
                },
                textSizeSmall: {
                    padding: "7px 12px",
                },
                textSizeMedium: {
                    padding: "9px 16px",
                },
                textSizeLarge: {
                    padding: "10px 16px",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    border: "0px",
                    textDecoration: "underline #BEBEBE",
                    padding: 0,
                    margin: 0,
                    fontSize: "0.85rem",
                    fontWeight: 400,
                    letterSpacing: 1,
                    lineHeight: 1,
                },
            },
        },
        MuiInputBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    background: "white",
                },
            },
        },
        MuiContainer: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    display: "flex",
                    padding: "32px 16px",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "24px",
                    alignSelf: "stretch",
                    minHeight: "100vh",
                },
            },
        },
        MuiCard: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: "8px",
                    background: "#FFF",
                    padding: "16px",
                },
            },
        },

        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    textTransform: "none",
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: "0px 0px",
                    "&:last-child": {
                        paddingBottom: "0px",
                    },
                },
            },
        },
        MuiCardHeader: {
            defaultProps: {
                titleTypographyProps: {
                    variant: "h6",
                },
                subheaderTypographyProps: {
                    variant: "body2",
                },
            },
            styleOverrides: {
                root: {
                    padding: "32px 24px",
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                "*": {
                    boxSizing: "border-box",
                    margin: 0,
                    padding: 0,
                },
                html: {
                    MozOsxFontSmoothing: "grayscale",
                    WebkitFontSmoothing: "antialiased",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100%",
                    width: "100%",
                },
                body: {
                    display: "flex",
                    flex: "1 1 auto",
                    flexDirection: "column",
                    minHeight: "100%",
                    width: "100%",
                },
                "#__next": {
                    display: "flex",
                    flex: "1 1 auto",
                    flexDirection: "column",
                    height: "100%",
                    width: "100%",
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {},
                notchedOutline: {
                    borderColor: "#E6E8F0",
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "purple",
                    },
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: "#F3F4F6",
                    ".MuiTableCell-root": {
                        color: "#374151",
                    },
                    borderBottom: "none",
                    "& .MuiTableCell-root": {
                        borderBottom: "none",
                        fontSize: "12px",
                        fontWeight: 600,
                        lineHeight: 1,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                    },
                    "& .MuiTableCell-paddingCheckbox": {
                        paddingTop: 4,
                        paddingBottom: 4,
                    },
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                button: {
                    textDecoration: "none",
                    color: "text.main",
                    fontSize: "0.80rem",
                    fontWeight: 500,
                },
                link: {
                    textDecoration: "none",
                    color: "text.main",
                    fontSize: "0.80rem",
                    fontWeight: 500,
                },
                fontFamily:
                    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
                body1: {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    lineHeight: 1.5,
                },
                body2: {
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    lineHeight: 1.57,
                },
                subtitle: {
                    fontSize: "1.10rem",
                    fontStyle: "normal",
                    fontWeight: 500,
                    letterSpacing: 1.15,
                    lineHeight: 1.375,
                },
                subtitle1: {
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    lineHeight: 1,
                },

                subtitle2: {
                    fontSize: "1.0rem",
                    fontWeight: 500,
                    lineHeight: 1,
                },
                overline: {
                    fontSize: "1rem",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    lineHeight: 1,
                    // textTransform: 'uppercase',
                    color: "#088AB2",
                },
                caption: {
                    fontSize: "0.9rem",
                    fontWeight: 400,
                    lineHeight: 1,
                },
                h1: {
                    fontWeight: 700,
                    fontSize: "3.5rem",
                    lineHeight: 1.375,
                },
                h2: {
                    fontSize: "1.5rem",
                    fontWeight: 400,
                    lineHeight: 1,
                },
                h3: {
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    lineHeight: 1.375,
                },
                h4: {
                    fontWeight: 700,
                    fontSize: "2rem",
                    lineHeight: 1.375,
                },
                h5: {
                    fontSize: "1.35rem",
                    fontStyle: "normal",
                    fontWeight: 100,
                    lineHeight: 1.375,
                    letterSpacing: 1,
                },
                h6: {
                    fontWeight: 600,
                    fontSize: "1.25rem",
                    lineHeight: 1.375,
                },
                ellipsis: {
                    width: "200px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                },
            },
        },
    },

    shadows: [
        "none",
        "0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)",
        "0px 1px 2px rgba(100, 116, 139, 0.12)",
        "0px 1px 4px rgba(100, 116, 139, 0.12)",
        "0px 1px 5px rgba(100, 116, 139, 0.12)",
        "0px 1px 6px rgba(100, 116, 139, 0.12)",
        "0px 2px 6px rgba(100, 116, 139, 0.12)",
        "0px 3px 6px rgba(100, 116, 139, 0.12)",
        "0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)",
        "0px 5px 12px rgba(100, 116, 139, 0.12)",
        "0px 5px 14px rgba(100, 116, 139, 0.12)",
        "0px 5px 15px rgba(100, 116, 139, 0.12)",
        "0px 6px 15px rgba(100, 116, 139, 0.12)",
        "0px 7px 15px rgba(100, 116, 139, 0.12)",
        "0px 8px 15px rgba(100, 116, 139, 0.12)",
        "0px 9px 15px rgba(100, 116, 139, 0.12)",
        "0px 10px 15px rgba(100, 116, 139, 0.12)",
        "0px 12px 22px -8px rgba(100, 116, 139, 0.25)",
        "0px 13px 22px -8px rgba(100, 116, 139, 0.25)",
        "0px 14px 24px -8px rgba(100, 116, 139, 0.25)",
        "0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)",
        "0px 25px 50px rgba(100, 116, 139, 0.25)",
        "0px 25px 50px rgba(100, 116, 139, 0.25)",
        "0px 25px 50px rgba(100, 116, 139, 0.25)",
        "0px 25px 50px rgba(100, 116, 139, 0.25)",
    ],
});
